import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '../api/hooks.js';
import { setWorkspaceId, useWorkspaceId } from '../workspace.js';
import type { ApiMe } from '../api/types.js';
import CreateWorkspaceModal from './CreateWorkspaceModal.js';

export default function AppLayout({
  me,
  children,
}: { me: ApiMe; children: React.ReactNode }) {
  const [creating, setCreating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer whenever the route changes — otherwise it
  // stays open and covers the page they navigated to.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="flex h-full flex-col bg-slate-50 md:flex-row">
      {/* Mobile top bar — only shown on small screens. */}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <Link to="/app/dashboard" className="text-lg font-semibold tracking-tight">
          adhc<span className="text-brand-600">.to</span>
        </Link>
        <button
          type="button"
          className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </header>

      {/* Off-canvas mobile drawer. Hidden when drawerOpen=false. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          {/* Backdrop swallows clicks-outside; hidden from the a11y tree so
              the X button stays the sole "Close menu" affordance for AT. */}
          <div
            className="absolute inset-0 bg-slate-900/40"
            aria-hidden="true"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 pt-5 pb-3">
              <Link to="/app/dashboard" className="text-lg font-semibold tracking-tight">
                adhc<span className="text-brand-600">.to</span>
              </Link>
              <button
                type="button"
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
              >
                ✕
              </button>
            </div>
            <SidebarContent
              me={me}
              onCreateWorkspace={() => { setDrawerOpen(false); setCreating(true); }}
            />
          </aside>
        </div>
      )}

      {/* Permanent desktop sidebar. */}
      <aside className="hidden border-r border-slate-200 bg-white md:flex md:w-60 md:flex-col">
        <Link to="/app/dashboard" className="px-5 pt-5 pb-3 text-lg font-semibold tracking-tight">
          adhc<span className="text-brand-600">.to</span>
        </Link>
        <SidebarContent
          me={me}
          onCreateWorkspace={() => setCreating(true)}
        />
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
          {children}
        </div>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 pb-6 text-xs text-slate-500 sm:px-6 lg:px-8">
          <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          <a href="mailto:support@adhoc.space" className="hover:text-slate-700">Report an issue</a>
        </div>
      </main>

      <CreateWorkspaceModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function SidebarContent({
  me,
  onCreateWorkspace,
}: { me: ApiMe; onCreateWorkspace: () => void }) {
  const workspaceId = useWorkspaceId();
  const logout = useLogout();
  const navigate = useNavigate();
  const current = me.workspaces.find((w) => w.id === workspaceId) ?? me.workspaces[0];

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-3">
        <label className="label">Workspace</label>
        <select
          className="input mt-1"
          value={current?.id ?? ''}
          onChange={(e) => setWorkspaceId(e.target.value)}
        >
          {me.workspaces.map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-500">Role: {current?.role}</p>
        <button
          type="button"
          className="mt-2 w-full rounded-md border border-dashed border-slate-300 px-2 py-1.5 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-600"
          onClick={onCreateWorkspace}
        >
          + New workspace
        </button>
      </div>
      <nav className="mt-2 flex flex-col gap-1 px-3 text-sm">
        <SidebarLink to="/app/dashboard">Links</SidebarLink>
        <SidebarLink to="/app/workspace">Workspace</SidebarLink>
        <SidebarLink to="/app/settings">Settings</SidebarLink>
      </nav>
      <div className="mt-auto border-t border-slate-100 p-4">
        <p className="truncate text-sm font-medium text-slate-700">
          {me.user.name ?? me.user.email}
        </p>
        <p className="truncate text-xs text-slate-500">{me.user.email}</p>
        <button
          className="btn-ghost mt-2 w-full justify-start text-sm"
          onClick={async () => {
            await logout.mutateAsync();
            setWorkspaceId(null);
            navigate('/login');
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function SidebarLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-lg px-3 py-2 transition ${
          isActive
            ? 'bg-brand-50 font-medium text-brand-700'
            : 'text-slate-600 hover:bg-slate-100'
        }`
      }
    >
      {children}
    </NavLink>
  );
}
